import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { IntegrationV2Query, IntegrationTypeV2, RedoCredentialsModel } from '@/gql/graphql';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { INTEGRATION_V2_QUERY } from '@/queries/integration';
import { useState, useEffect } from 'react';
import { SaveButton } from '../../components/save-button';
import {
  FormLayout,
  FormSection,
  InputField,
  PasswordField
} from '../../components/tracking-details-form/base';
import {
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';

import { useIntegrationUpdater } from '../../hooks/integration-updater';

const formSchema = z.object({
  redoAccessToken: z.string().min(1, 'Access Token is required.'),
  redoStoreId: z.string().min(1, 'Redo Store Id is Required'),
  redoWidgetId: z.string().optional(),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional()
});

const eventsList = getEventsList(IntegrationTypeV2.Redo);

export const RedoSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const [isUpdating, setIsUpdating] = useState(false);
  const { updateIntegration } = useIntegrationUpdater();

  const { data } = useQuery<IntegrationV2Query>(INTEGRATION_V2_QUERY, {
    variables: { id: params.id! }
  });

  const isInitialSetup = !(data?.integrationV2.credentials as RedoCredentialsModel | undefined)
    ?.redoAccessToken;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      redoAccessToken: '',
      redoStoreId: '',
      redoWidgetId: '',
      consentMode: false,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isUpdating && data?.integrationV2) {
      const integration = data.integrationV2;
      const credentials = integration.credentials as RedoCredentialsModel | undefined;
      form.reset({
        redoAccessToken: credentials?.redoAccessToken ?? '',
        redoStoreId: credentials?.redoStoreId ?? '',
        redoWidgetId: credentials?.redoWidgetId ?? '',
        consentMode: integration.consentMode ?? false,
        customEvents: getUserSelectedEvents(data)
      });
    }
  }, [data, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsUpdating(true);
      await updateIntegration({
        id: params.id!,
        credentials: {
          redoAccessToken: values.redoAccessToken,
          redoStoreId: values.redoStoreId,
          redoWidgetId: values.redoWidgetId ?? undefined
        },
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode
      });
      toast({
        title: 'Form submitted',
        description: 'Your Redo integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to save Redo integration settings.',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Redo Settings">
            In order to send transactions data to this destination, we need the following
            information from your Redo account.
          </FormHeaderSection>
          <FormSection columns={2}>
            <PasswordField form={form} name="redoAccessToken" label="Redo Access Token" />
            <InputField
              form={form}
              name="redoStoreId"
              label="Redo Store Id"
              placeholder="e.g. 123456789012345"
            />
            <InputField
              form={form}
              name="redoWidgetId"
              label="Redo Widget Id (optional)"
              placeholder="Pixel strategy only; leave blank to fall back to Store Id"
            />
          </FormSection>
          <ConsentModeSection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() => getUserSelectedEvents(data)}
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isUpdating} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
